// src/AuthContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { supabase } from "./supabaseClient";
import { clearAIDrafts } from "./components/workspace/aiDraftMemory";
import { clearAdminSectionCache } from "./components/admin/adminSectionCache";

const AuthContext = createContext({
  user: null,
  loading: true,
  authError: null,
  profile: null,
  accountType: null,
  refreshProfile: () => {},
  accountSuspension: null,
  suspensionLoading: false,
  suspensionError: null,
  refreshSuspension: () => {},
  accountWarning: null,
  warningLoading: false,
  warningError: null,
  refreshWarning: () => {},
});

const AUTH_REQUEST_TIMEOUT_MS = 15000;
const boundedAuthRequest = async (request) => {
  let timer;
  try {
    return await Promise.race([
      request,
      new Promise((_, reject) => {
        timer = window.setTimeout(
          () => reject(new Error("Account check timed out")),
          AUTH_REQUEST_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [accountSuspension, setAccountSuspension] = useState(null);
  const [suspensionLoading, setSuspensionLoading] = useState(false);
  const [suspensionError, setSuspensionError] = useState(null);
  const [accountWarning, setAccountWarning] = useState(null);
  const [warningLoading, setWarningLoading] = useState(false);
  const [warningError, setWarningError] = useState(null);
  const mounted = useRef(false);
  const activeUserId = useRef(null);
  const suspensionRequest = useRef(0);
  const warningRequest = useRef(0);
  const profileRequest = useRef(0);

  const loadSuspension = useCallback(async (uid) => {
    if (!mounted.current || (uid || null) !== activeUserId.current)
      return { active: false, error: true, stale: true };
    const request = ++suspensionRequest.current;
    const current = () =>
      mounted.current &&
      request === suspensionRequest.current &&
      (uid || null) === activeUserId.current;
    if (!current()) return { active: false, error: true, stale: true };
    if (!uid) {
      setAccountSuspension(null);
      setSuspensionLoading(false);
      setSuspensionError(null);
      return { active: false, error: false };
    }
    setSuspensionLoading(true);
    try {
      const { data, error } = await boundedAuthRequest(
        supabase
          .from("account_suspensions")
          .select("reason,is_active,restriction_type,suspended_at,expires_at")
          .eq("profile_id", uid)
          .maybeSingle(),
      );
      if (!current()) return { active: false, error: true, stale: true };
      if (error) throw error;
      const active =
        data?.is_active &&
        (!data.expires_at || new Date(data.expires_at).getTime() > Date.now());
      setAccountSuspension(active ? data : null);
      setSuspensionError(null);
      return { active: Boolean(active), error: false };
    } catch (error) {
      if (!current()) return { active: false, error: true, stale: true };
      console.error("loadSuspension error:", error);
      setSuspensionError("We could not verify account access. Please retry.");
      return { active: false, error: true };
    } finally {
      if (current()) setSuspensionLoading(false);
    }
  }, []);

  const loadWarning = useCallback(async (uid) => {
    if (!mounted.current || (uid || null) !== activeUserId.current) return;
    const request = ++warningRequest.current;
    const current = () =>
      mounted.current &&
      request === warningRequest.current &&
      (uid || null) === activeUserId.current;
    if (!current()) return;
    if (!uid) {
      setAccountWarning(null);
      setWarningLoading(false);
      setWarningError(null);
      return;
    }
    setWarningLoading(true);
    try {
      const { data, error } = await boundedAuthRequest(
        supabase.functions.invoke("admin_billing_actions", {
          body: { action: "load_my_account_warning" },
        }),
      );
      if (!current()) return;
      if (error || data?.error) throw error || Error(data.error);
      if (!data?.ok || typeof data.account_restricted !== "boolean")
        throw Error("Account notice response was incomplete");
      if (data.account_restricted) {
        setAccountWarning(null);
        setWarningError(null);
        setSuspensionError(
          "Your account access changed. Please retry the account check.",
        );
        return;
      }
      setAccountWarning(
        data?.account_restricted ? null : data?.warning || null,
      );
      setWarningError(null);
    } catch (error) {
      if (!current()) return;
      console.error("loadWarning error:", error);
      setAccountWarning(null);
      setWarningError("We could not load your account notice. Please retry.");
    } finally {
      if (current()) setWarningLoading(false);
    }
  }, []);

  const loadProfile = useCallback(async (uid) => {
    if (!mounted.current || (uid || null) !== activeUserId.current) return;
    const request = ++profileRequest.current;
    const current = () =>
      mounted.current &&
      request === profileRequest.current &&
      (uid || null) === activeUserId.current;
    if (!current()) return;
    if (!uid) {
      setProfile(null);
      setAccountType(null);
      return;
    }

    try {
      const { data, error } = await boundedAuthRequest(
        supabase
          .from("profiles")
          .select("*") // fetch ALL fields: includes region, address, etc.
          .eq("id", uid)
          .maybeSingle(),
      );
      if (!current()) return;

      if (error) {
        // Keep whatever we already had from user_metadata; avoid flicker
        console.error("loadProfile error:", error);
        return;
      }

      if (data) {
        setProfile(data);
        setAccountType(data.account_type ?? null);
      }
    } catch (error) {
      if (current()) console.error("loadProfile error:", error);
    }
  }, []);

  const refreshProfile = () => {
    return loadProfile(user?.id || null);
  };

  useEffect(() => {
    let alive = true;
    let authEventReceived = false;
    let generation = 0;
    const deferred = new Set();
    mounted.current = true;

    const applySession = (session) => {
      if (!alive) return;
      const nextUser = session?.user || null;
      const uid = nextUser?.id || null;
      const changedUser = activeUserId.current !== uid;
      activeUserId.current = uid;
      const currentGeneration = ++generation;
      // Invalidate old requests before publishing another identity.
      suspensionRequest.current += 1;
      warningRequest.current += 1;
      profileRequest.current += 1;
      if (changedUser || !uid) {
        clearAdminSectionCache();
        setProfile(null);
        setAccountSuspension(null);
        setSuspensionError(null);
        setAccountWarning(null);
        setWarningError(null);
      }
      if (!uid) clearAIDrafts();
      // Auth is ready once its session is known. The existing App account gate
      // remains blocked by these separate checks until access is verified.
      setSuspensionLoading(Boolean(uid));
      setWarningLoading(Boolean(uid));
      setUser(nextUser);
      setAuthError(null);
      setAccountType(nextUser?.user_metadata?.account_type ?? null);
      setLoading(false);

      // Supabase awaits auth listeners while holding its session lock. Keep the
      // listener synchronous and start requests after that callback has returned.
      const timer = window.setTimeout(() => {
        deferred.delete(timer);
        if (!alive || currentGeneration !== generation) return;
        void loadProfile(uid);
        void loadSuspension(uid).then((access) => {
          if (!alive || currentGeneration !== generation || access?.stale)
            return;
          if (uid && !access.active && !access.error) return loadWarning(uid);
          setAccountWarning(null);
          setWarningError(null);
          setWarningLoading(false);
          return undefined;
        });
      }, 0);
      deferred.add(timer);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      authEventReceived = true;
      applySession(session);
    });
    void boundedAuthRequest(supabase.auth.getSession())
      .then(({ data, error }) => {
        if (!alive || authEventReceived) return;
        if (error) throw error;
        applySession(data?.session || null);
      })
      .catch((error) => {
        if (!alive || authEventReceived) return;
        console.error("getSession error:", error);
        // Unknown sessions remain signed out; protected routes cannot open.
        setAuthError('We could not check your sign-in session. Reload this page to try again.');
        setLoading(false);
      });

    return () => {
      alive = false;
      mounted.current = false;
      suspensionRequest.current += 1;
      warningRequest.current += 1;
      profileRequest.current += 1;
      deferred.forEach((timer) => window.clearTimeout(timer));
      // Unsubscribe auth listener
      try {
        sub?.subscription?.unsubscribe();
      } catch {}
    };
  }, [loadProfile, loadSuspension, loadWarning]);

  const refreshSuspension = useCallback(async () => {
    const access = await loadSuspension(user?.id || null);
    if (access?.stale) return access;
    if (user?.id && access && !access.active && !access.error)
      await loadWarning(user.id);
    else {
      setAccountWarning(null);
      setWarningError(null);
      setWarningLoading(false);
    }
    return access;
  }, [user?.id, loadSuspension, loadWarning]);
  const refreshWarning = useCallback(
    () => loadWarning(user?.id || null),
    [user?.id, loadWarning],
  );

  useEffect(() => {
    if (!user?.id || !accountSuspension?.expires_at) return;
    const delay = Math.max(
      0,
      new Date(accountSuspension.expires_at).getTime() - Date.now() + 100,
    );
    const timer = window.setTimeout(() => {
      void refreshSuspension();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [user?.id, accountSuspension?.expires_at, refreshSuspension]);

  useEffect(() => {
    if (!user?.id) return;
    const refreshOnReturn = () => {
      if (document.visibilityState === "visible") void refreshSuspension();
    };
    window.addEventListener("focus", refreshOnReturn);
    document.addEventListener("visibilitychange", refreshOnReturn);
    const channel = supabase
      .channel(`account-suspensions-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "account_suspensions",
          filter: `profile_id=eq.${user.id}`,
        },
        () => {
          void refreshSuspension();
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "account_warnings",
          filter: `profile_id=eq.${user.id}`,
        },
        () => {
          void loadWarning(user.id);
        },
      )
      .subscribe();
    return () => {
      window.removeEventListener("focus", refreshOnReturn);
      document.removeEventListener("visibilitychange", refreshOnReturn);
      try {
        supabase.removeChannel(channel);
      } catch {}
    };
  }, [user?.id, refreshSuspension, loadWarning]);

  // Realtime updates for this user's profile row
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`profiles-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          if (!mounted.current || activeUserId.current !== user.id) return;
          const row = payload.new || payload.old;
          if (row) {
            setProfile((prev) => ({ ...(prev || {}), ...row }));
            if ("account_type" in row) setAccountType(row.account_type ?? null);
          }
        },
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {}
    };
  }, [user?.id]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authError,
        profile,
        accountType,
        refreshProfile,
        accountSuspension,
        suspensionLoading,
        suspensionError,
        refreshSuspension,
        accountWarning,
        warningLoading,
        warningError,
        refreshWarning,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
